import type {
  MarketingCohort,
  MarketingNodeType,
  MarketingStep,
} from "./types";

/**
 * Модель сценария на фронте — зеркало go/retention/marketing_nodes.go.
 *
 * Сценарий лежит в том же массиве шагов, что и раньше: позиция узла в массиве
 * остаётся его номером на бэкенде. Узел без типа — сообщение, узел без явного
 * перехода ведёт на следующий по порядку. Поэтому линейная цепочка, собранная
 * до появления сценариев, открывается тут же и выглядит прямой ниткой.
 *
 * Главное правило, которое проверяет и бэкенд: переходы только ВПЕРЁД по
 * массиву. Это и есть защита от зацикливания.
 */

export const NODE_LABELS: Record<MarketingNodeType, string> = {
  message: "Действие",
  delay: "Ожидание",
  segmentSplit: "Условие",
  randomCohort: "A/B-тест",
  randomSplit: "Разделение",
  contactLimit: "Лимит контактов",
  exit: "Завершение",
};

export function nodeType(step: MarketingStep): MarketingNodeType {
  return step.type ?? "message";
}

export function isMessage(step: MarketingStep): boolean {
  return nodeType(step) === "message";
}

export function messageCount(steps: MarketingStep[]): number {
  return steps.filter(isMessage).length;
}

export function indexById(steps: MarketingStep[], id?: string): number {
  if (!id) return -1;
  return steps.findIndex((step) => step.id === id);
}

/** Куда ведёт ветка. Пустой идентификатор — следующий узел по порядку. */
export function branchTarget(
  steps: MarketingStep[],
  from: number,
  stepId?: string,
): number {
  if (!stepId) return from + 1;
  const target = indexById(steps, stepId);
  return target <= from ? from + 1 : target;
}

export interface ScenarioEdge {
  from: number;
  to: number;
  /** Подпись на связи: «подходит», «40%» и т.п. Пусто — обычный переход. */
  label?: string;
  /** Какую ветку узла меняет связь — нужно при перетаскивании стрелки. */
  branch: "next" | "true" | "false" | `cohort:${number}`;
}

/** Все связи сценария. Узлы за пределами массива отбрасываются: это конец. */
export function scenarioEdges(steps: MarketingStep[]): ScenarioEdge[] {
  const out: ScenarioEdge[] = [];
  const push = (
    from: number,
    stepId: string | undefined,
    branch: ScenarioEdge["branch"],
    label?: string,
  ) => {
    const to = branchTarget(steps, from, stepId);
    if (to >= 0 && to < steps.length) out.push({ from, to, branch, label });
  };
  steps.forEach((step, index) => {
    switch (nodeType(step)) {
      case "exit":
        break;
      case "segmentSplit":
        push(index, step.split?.trueStepId, "true", "подходит");
        push(index, step.split?.falseStepId, "false", "не подходит");
        break;
      case "contactLimit":
        push(index, step.contactLimit?.passStepId, "true", "можно отправить");
        push(index, step.contactLimit?.stopStepId, "false", "лимит исчерпан");
        break;
      case "randomSplit":
      case "randomCohort":
        (step.cohorts ?? []).forEach((cohort, n) =>
          push(index, cohort.stepId, `cohort:${n}`, `${cohort.percent}%`),
        );
        break;
      default:
        push(index, step.nextStepId, "next");
    }
  });
  return out;
}

/** Проставить ветке новую цель. Возвращает новый массив шагов. */
export function setBranch(
  steps: MarketingStep[],
  from: number,
  branch: ScenarioEdge["branch"],
  targetId: string | undefined,
): MarketingStep[] {
  return steps.map((step, index) => {
    if (index !== from) return step;
    if (branch === "next") return { ...step, nextStepId: targetId };
    if (branch === "true" || branch === "false") {
      if (nodeType(step) === "contactLimit")
        return {
          ...step,
          contactLimit: {
            max: step.contactLimit?.max ?? 2,
            ...step.contactLimit,
            [branch === "true" ? "passStepId" : "stopStepId"]: targetId,
          },
        };
      const split = step.split ?? { condition: emptyCondition() };
      return {
        ...step,
        split: {
          ...split,
          [branch === "true" ? "trueStepId" : "falseStepId"]: targetId,
        },
      };
    }
    const n = Number(branch.split(":")[1]);
    const cohorts = (step.cohorts ?? []).map((cohort, i) =>
      i === n ? { ...cohort, stepId: targetId } : cohort,
    );
    return { ...step, cohorts };
  });
}

export function emptyCondition(): NonNullable<
  MarketingStep["split"]
>["condition"] {
  return { kind: "group", op: "all", children: [] };
}

/** Доли когорт должны складываться в 100 — так же проверяет бэкенд. */
export function cohortsValid(cohorts: MarketingCohort[] | undefined): boolean {
  if (!cohorts || cohorts.length < 2 || cohorts.length > 5) return false;
  const total = cohorts.reduce((sum, c) => sum + c.percent, 0);
  return (
    total === 100 && cohorts.every((c) => c.percent >= 1 && c.percent <= 99)
  );
}

/**
 * Проверки сценария теми же словами, что и на бэкенде, — чтобы человек видел
 * ошибку в редакторе, а не после нажатия «Сохранить».
 */
export function scenarioErrors(steps: MarketingStep[]): string[] {
  const errors: string[] = [];
  const messages = messageCount(steps);
  if (messages < 1)
    errors.push("В сценарии должно быть хотя бы одно сообщение");
  if (messages > 10) errors.push("В сценарии не больше 10 сообщений");
  if (steps.length > 30) errors.push("В сценарии не больше 30 узлов");
  if (steps.length && nodeType(steps[0]) === "exit") {
    errors.push("Сценарий не может начинаться с выхода");
  }
  const forward = (from: number, id: string | undefined, what: string) => {
    if (!id) return;
    const target = indexById(steps, id);
    if (target < 0) errors.push(`${what}: переход ведёт в никуда`);
    else if (target <= from) errors.push(`${what}: переход ведёт назад`);
  };
  steps.forEach((step, index) => {
    const title = `${NODE_LABELS[nodeType(step)]} №${index + 1}`;
    forward(index, step.nextStepId, title);
    const minutes = step.delayMinutes ?? step.waitHours * 60;
    if (
      nodeType(step) === "delay" &&
      (!Number.isInteger(minutes) || minutes < 1 || minutes > 86400)
    ) {
      errors.push(`${title}: пауза от 1 минуты до 60 суток`);
    }
    if (nodeType(step) === "segmentSplit") {
      forward(index, step.split?.trueStepId, `${title}, ветка «подходит»`);
      forward(index, step.split?.falseStepId, `${title}, ветка «не подходит»`);
    }
    if (nodeType(step) === "contactLimit") {
      if (
        !Number.isInteger(step.contactLimit?.max) ||
        step.contactLimit!.max < 1 ||
        step.contactLimit!.max > 10
      )
        errors.push(`${title}: от 1 до 10 контактов`);
      forward(
        index,
        step.contactLimit?.passStepId,
        `${title}, можно отправить`,
      );
      forward(index, step.contactLimit?.stopStepId, `${title}, лимит исчерпан`);
    }
    if (["randomCohort", "randomSplit"].includes(nodeType(step))) {
      if (!cohortsValid(step.cohorts)) {
        errors.push(`${title}: от 2 до 5 веток, доли в сумме 100%`);
      }
      (step.cohorts ?? []).forEach((cohort, n) =>
        forward(index, cohort.stepId, `${title}, ветка ${n + 1}`),
      );
    }
  });
  const edges = scenarioEdges(steps);
  const reachable = new Set<number>();
  const queue = steps.length ? [0] : [];
  while (queue.length) {
    const current = queue.pop()!;
    if (reachable.has(current)) continue;
    reachable.add(current);
    edges
      .filter((edge) => edge.from === current)
      .forEach((edge) => queue.push(edge.to));
    const unreadTarget = indexById(
      steps,
      steps[current]?.unreadFallback?.targetStepId,
    );
    if (unreadTarget > current) queue.push(unreadTarget);
  }
  if (
    steps.some(
      (step, index) => nodeType(step) !== "exit" && !reachable.has(index),
    )
  ) {
    errors.push("Сценарий: соедините все блоки с событием");
  }
  return errors;
}

export const NODE_WIDTH = 240;
const NODE_HEIGHT = 96;
const GAP_X = 96;
const GAP_Y = 64;

/**
 * Раскладка узлов, если координат ещё нет: слои по глубине, внутри слоя — в
 * ряд. Даёт вертикальную схему, как в прежнем списке шагов, а ветки расходятся
 * вбок.
 */
export function layoutScenario(
  steps: MarketingStep[],
): Array<{ x: number; y: number }> {
  const edges = scenarioEdges(steps);
  const depth = steps.map(() => 0);
  // Переходы только вперёд, поэтому один проход по возрастанию индекса даёт
  // корректную глубину каждого узла.
  edges.forEach(({ from, to }) => {
    depth[to] = Math.max(depth[to], depth[from] + 1);
  });
  const perLevel = new Map<number, number>();
  return steps.map((step, index) => {
    if (typeof step.canvasX === "number" && typeof step.canvasY === "number") {
      return { x: step.canvasX, y: step.canvasY };
    }
    const level = depth[index];
    const column = perLevel.get(level) ?? 0;
    perLevel.set(level, column + 1);
    return {
      x: column * (NODE_WIDTH + GAP_X),
      y: level * (NODE_HEIGHT + GAP_Y),
    };
  });
}

/** Есть ли в сценарии что-то, кроме прямой цепочки сообщений. */
export function hasBranches(steps: MarketingStep[]): boolean {
  return steps.some((step) => !isMessage(step) || !!step.nextStepId);
}

/**
 * Удалить узел и починить ссылки на него: ветки, которые вели в удалённый узел,
 * возвращаются к «следующему по порядку». Иначе сценарий останется с переходом
 * в никуда, а это ошибка и на бэкенде.
 */
export function removeNode(
  steps: MarketingStep[],
  id: string,
): MarketingStep[] {
  const clean = (target?: string) => (target === id ? undefined : target);
  return steps
    .filter((step) => step.id !== id)
    .map((step) => {
      const next: MarketingStep = {
        ...step,
        nextStepId: clean(step.nextStepId),
      };
      if (step.split) {
        next.split = {
          ...step.split,
          trueStepId: clean(step.split.trueStepId),
          falseStepId: clean(step.split.falseStepId),
        };
      }
      if (step.contactLimit)
        next.contactLimit = {
          ...step.contactLimit,
          passStepId: clean(step.contactLimit.passStepId),
          stopStepId: clean(step.contactLimit.stopStepId),
        };
      if (step.cohorts) {
        next.cohorts = step.cohorts.map((cohort) => ({
          ...cohort,
          stepId: clean(cohort.stepId),
        }));
      }
      return next;
    });
}
